import * as React from 'react'
import { b, constVoid, DOM, E, identity, IOO, O, pipe, RA, RD, RR, s, t, TE } from '@packages/prelude'
import { css, styled } from 'styled-components'
import { Col, Container as BSContainer, Row } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload, faRocket, faRotateRight, faShoppingCart } from '@fortawesome/pro-solid-svg-icons'
import * as domain from '@packages/domain'
import ImageGallery from 'react-image-gallery'
import { buildProductConfig, selectionOf, toRequestBody } from './state/productConfig'
import { addonTotal } from './state/pricing'
import { numberedSteps, resolveHeadings } from './state/stepHeadings'
import { StepHeading } from './StepHeading'
import { usePreviewSession } from './hooks/usePreviewSession'
import { useGreetingFields } from './hooks/useGreetingFields'
import { useUserImageFields } from './hooks/useUserImageFields'
import { useOptions } from './hooks/useOptions'
import { PreviewAds } from './PreviewAds'
import { PreviewError } from './PreviewError'
import { usePreviewAdsGate } from './hooks/usePreviewAdsGate'
import { useHintError } from './hooks/useHintError'
import { AudioSegments, useAudioSegmentsState, AudioSegmentsState } from '~/components/AudioSegments'
import { useApi } from '~/api'
import { PreviewData } from '~/hooks/useIndiAudioPreview'
import { AudioPlayer, ShareWidget, Loading, ModalHint, StarGreeting, ImageUpload } from '~/components'
import { ControlButton } from '~/components/Buttons'
import { react } from '~/commons'

/**
 * Components
 */

const Container = styled(BSContainer)`
  position: relative;
  min-height: 400px;
`

const Title = styled.h2`
  margin-top: 6rem;
`

const Description = styled.div`
  font-size: 1.25rem;
  margin-bottom: 1rem;
  ul {
    margin-left: 1rem;
  }
`

const Image = styled.img`
  width: 100%;
`

const Wrapper = styled.div`
  position: relative;
  border-radius: 3rem;
  overflow: hidden;
`

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: var(--primary);
  mix-blend-mode: multiply;
`

const OrderButton = styled.button.attrs({
  className: 'btn btn-lg text-white text-uppercase fw-bold px-4 py-3 mt-5 align-self-stretch'
})<{ size?: 'lg' }>`
  ${props =>
    props.size === 'lg' &&
    css`
      font-size: 2rem !important;
    `}
`

const PreviewButton = styled.button.attrs({
  className: 'btn btn-lg text-white text-uppercase fw-bold px-4 py-3 align-self-stretch'
})<{ size?: 'lg'; className?: string }>`
  ${props =>
    props.size === 'lg' &&
    css`
      @media (min-width: 992px) {
        font-size: 2rem !important;
      }
    `}
`

const DownloadButton = styled.a.attrs({
  className: 'btn btn-lg text-white text-uppercase fw-bold px-4 py-3 mt-3'
})``

const ResetButton = styled(ControlButton).attrs({
  className: 'btn text-white text-uppercase fw-bold p-3 mt-3'
})`
  background-color: var(--button-color);
  position: absolute;
  right: -0.5rem;
  top: -1.5rem;
  @media (min-width: var(--bs-breakpoint-md)) {
    right: -1rem;
    top: -2rem;
  }
`

const fmtEur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' })

/**
 * Format entry
 */

const selectedFormatEntry = (
  fields: ReadonlyArray<domain.Meta.Fields.PersonalizationField.Type>
): O.Option<readonly [string, string]> =>
  pipe(
    fields,
    RA.findFirst(domain.Meta.Fields.PersonalizationField.ADT.is.format),
    O.chain(f =>
      pipe(
        f.defaultChoiceId,
        O.map(id => [f.id, id] as const)
      )
    )
  )

/**
 * Props
 */

interface Props extends domain.Product.Type {
  title?: string
  config: domain.Api.Config.Type
  initialData: O.Option<E.Either<domain.Error.Type, domain.Api.IALoadPreviewResponse.Type>>
}

/**
 * View
 */
export const View: React.FC<Props> = ({
  config,
  image,
  gallery,
  title,
  description,
  parameters,
  productId,
  price,
  meta,
  name,
  stepHeadings,
  initialData
}) => {
  const initialPreviewData = React.useMemo(
    () =>
      pipe(
        initialData,
        O.match(() => RD.initial, RD.fromEither)
      ),
    [initialData]
  )
  const loadedPreview = React.useMemo(() => pipe(initialData, O.chain(O.fromEither)), [initialData])
  const {
    previewData,
    generate: generatePreviewSession,
    reset: resetPreviewSession
  } = usePreviewSession({ productId, config, initialPreviewData })
  const { hintError, reportError, onModalHintHide } = useHintError({ initialData })
  const [segments, setSegments, resetSegments] = useAudioSegmentsState({
    meta,
    parameters,
    initialData: loadedPreview,
    onRestoreError: reportError
  })

  const isOrderable = React.useMemo(() => AudioSegmentsState.allRequiredAudioPresent(segments), [segments])
  const {
    starGreeting,
    onTextChange: onStarGreetingChange,
    onSelectToggle: onStarGreetingSelectToggle,
    reset: resetGreetings
  } = useGreetingFields({
    meta,
    parameters,
    initialData: loadedPreview
  })
  const {
    userImage,
    onImageUpload,
    onImageRemove,
    setRemoteUrl: setUserImageRemoteUrl,
    reset: resetUserImage
  } = useUserImageFields({
    meta,
    parameters,
    initialData: loadedPreview,
    onRestoreError: reportError
  })
  const { options } = useOptions()

  const optionsOption = pipe(options, RD.toOption)
  const steps = React.useMemo(
    () =>
      numberedSteps(
        resolveHeadings(
          O.fromNullable(stepHeadings),
          pipe(
            optionsOption,
            O.chainNullableK(o => o.stepHeadings)
          )
        ),
        { image: RR.size(userImage) > 0, message: true, preview: true }
      ),
    [stepHeadings, optionsOption, userImage]
  )
  const adSlides = pipe(
    optionsOption,
    O.chain(domain.Api.Ads.Optional.slides.getOption),
    O.getOrElseW(() => RA.empty)
  )
  const hasAds = adSlides.length > 0
  const adSlideInterval = pipe(
    optionsOption,
    O.chain(domain.Api.Ads.Optional.slide_interval.getOption),
    O.getOrElse(() => 4)
  )
  const adMinDisplay = pipe(
    optionsOption,
    O.chain(domain.Api.Ads.Optional.min_display_time.getOption),
    O.getOrElse(() => 0)
  )
  const nextLabel = pipe(
    optionsOption,
    O.chainNullableK(o => o.buttonLabel),
    O.chainNullableK(btnLabel => btnLabel.btn_next),
    O.getOrElse(() => 'Weiter')
  )
  const adsGate = usePreviewAdsGate({ previewData, minDisplaySeconds: adMinDisplay, hasAds })

  const [submitAddToCart] = useApi(domain.Api.Cart.addToCart)
  const [synthesize, ttsState] = useApi(domain.Api.ElevenLabsTTS.generateAudio)

  const productConfig = React.useMemo(
    () =>
      buildProductConfig({
        productId,
        quantity: 1,
        previewId: pipe(
          previewData,
          RD.toOption,
          O.map(data => data.transientId)
        ),
        formatEntry: selectedFormatEntry(meta.fields),
        segments,
        starGreeting,
        userImage
      }),
    [productId, previewData, meta.fields, segments, starGreeting, userImage]
  )

  const total = React.useMemo(
    () =>
      price +
      addonTotal(
        meta.fields,
        selectionOf({
          productId,
          quantity: 1,
          previewId: O.none,
          formatEntry: selectedFormatEntry(meta.fields),
          segments,
          starGreeting,
          userImage
        })
      ),
    [price, meta, productId, segments, starGreeting, userImage]
  )

  // scroll to product widget on initial load, if state was restored from transient (e.g. when sharing a widget)
  React.useEffect(() => {
    pipe(
      IOO.fromOption(loadedPreview),
      IOO.chain(({ product }) => pipe(document, DOM.querySelector(`#product-widget-${product}`))),
      IOO.chainIOK(widget => () => widget.scrollIntoView({ behavior: 'smooth' }))
    )()
  }, [loadedPreview])

  React.useEffect(() => {
    pipe(
      previewData,
      RD.match3W(
        () => constVoid,
        err => domain.Error.log(err)(),
        data =>
          pipe(
            data.files,
            RA.map(
              RR.mapWithIndex((id, file) =>
                pipe(
                  id,
                  domain.Error.decodeWith(t.NumberFromString),
                  E.match(
                    error => domain.Error.log(error)(),
                    paramId => {
                      // Blind-dispatch each preview file to both stores; the non-owning setter
                      // no-ops (segments ignore an image param, the image store ignores a segment).
                      setSegments(AudioSegmentsState.setRemoteUrl(paramId, file))
                      setUserImageRemoteUrl(paramId, file)
                    }
                  )
                )
              )
            )
          )
      )
    )
  }, [previewData, setSegments, setUserImageRemoteUrl])

  /*
    Callback functions
   */

  const previewPayload = React.useCallback(
    (segs: ReadonlyArray<domain.AudioSegment.Type>) => [
      ...AudioSegmentsState.toPreviewData(segs),
      ...pipe(
        starGreeting,
        RR.values,
        RA.filterMap(({ id, greeting, selected }) =>
          pipe(
            selected,
            O.fromPredicate(identity),
            O.chain(() => greeting),
            O.map(greeting => PreviewData.of.StarGreeting({ id, greeting }))
          )
        )
      ),
      ...pipe(
        userImage,
        RR.values,
        RA.filterMap(({ id, image }) =>
          pipe(
            image,
            O.map(image => PreviewData.of.UserImage({ id, image }))
          )
        )
      )
    ],
    [starGreeting, userImage]
  )

  const onPreviewClick = React.useCallback(
    () =>
      pipe(
        segments,
        AudioSegmentsState.synthesizeMissingGreetings(text => synthesize({ text })),
        TE.match(reportError, next => {
          setSegments(next)
          generatePreviewSession(previewPayload(next))
        })
      )(),
    [segments, synthesize, reportError, setSegments, generatePreviewSession, previewPayload]
  )

  const onResetClick = React.useCallback(() => {
    // reset to initial states — preserve per-field entry skeletons so the
    // corresponding UI controls keep rendering after reset
    resetPreviewSession()
    resetSegments()
    resetGreetings()
    resetUserImage()

    // remove possible hash from URL
    history.replaceState({}, document.title, window.location.pathname)
  }, [resetSegments, resetPreviewSession, resetGreetings, resetUserImage])

  const onOrderClick = React.useCallback(() => {
    const url = window.wc_add_to_cart_params.wc_ajax_url.replace('%%endpoint%%', 'add_to_cart')
    pipe(
      submitAddToCart({ url, body: toRequestBody(productConfig) }),
      TE.chainFirstIOK(() => () => {
        // update cart fragments
        jQuery(document.body).trigger('wc_fragment_refresh')
      }),
      TE.chainFirstIOK(() => () => {
        // reset to initial state
        onResetClick()
      })
    )()
  }, [submitAddToCart, productConfig, onResetClick])

  const galleryItems = React.useMemo(() => {
    const userImages = pipe(
      userImage,
      RR.values,
      RA.filterMap(data =>
        pipe(
          data.previewUrl,
          O.alt(() => data.remoteUrl),
          O.map(url => ({ original: url, thumbnail: url }))
        )
      )
    )
    return [...userImages, ...gallery]
  }, [userImage, gallery])

  return (
    <Container fluid="lg">
      <ModalHint title="Hinweis" show={O.isSome(hintError)} onHide={onModalHintHide}>
        {pipe(
          hintError,
          O.match(
            () => <></>,
            domain.Error.Type.match(
              {
                AudioNotFoundError: () => (
                  <p>
                    <strong>Die Audio-Aufnahme wurde nicht gefunden</strong>
                    <br />
                    Möglicherweise wurde sie gelöscht oder ist abgelaufen.
                  </p>
                )
              },
              () => (
                <p>
                  <strong>Die Vorschau konnte nicht geladen werden</strong>
                  <br />
                  Bitte versuche es erneut oder kontaktiere den Support, falls das Problem bestehen bleibt.
                </p>
              )
            )
          )
        )}
      </ModalHint>
      <Row>
        <Col lg={{ offset: 4, span: 7 }} className="ps-lg-5">
          <ResetButton onClick={onResetClick}>
            <FontAwesomeIcon icon={faRotateRight} />
          </ResetButton>
          {pipe(
            O.fromNullable(title),
            O.foldMap(react.monoid)(title => <Title>{title}</Title>)
          )}
        </Col>
      </Row>
      {adsGate.visible ? (
        <PreviewAds
          slides={adSlides}
          slideInterval={adSlideInterval}
          canAdvance={adsGate.canAdvance}
          nextLabel={nextLabel}
          onAdvance={adsGate.advance}
        />
      ) : (
        pipe(
          previewData,
          RD.fold(
            () => (
              <Row>
                <Col lg={4} className="d-flex flex-column align-items-center">
                  <div className="px-lg-5">
                    {pipe(
                      steps.image,
                      O.foldMap(react.monoid)(s => <StepHeading key="step-image" n={s.n} text={s.text} />)
                    )}
                    <Wrapper>
                      <ImageGallery items={galleryItems} showThumbnails={false} showPlayButton={false} />
                    </Wrapper>
                    {pipe(
                      userImage,
                      RR.foldMapWithIndex(s.Ord)(react.monoid)((key, data) => (
                        <ImageUpload
                          key={key}
                          onChange={onImageUpload(key)}
                          onRemove={onImageRemove(key)}
                          previewUrl={data.previewUrl}
                          readOnly={O.isSome(data.remoteUrl)}
                        />
                      ))
                    )}
                  </div>
                </Col>
                <Col lg={8} xl={7} className="d-flex flex-column align-items-center gap-3 ps-md-5 mt-5 mb-5">
                  {pipe(
                    O.fromNullable(description),
                    O.foldMap(react.monoid)(description => <Description dangerouslySetInnerHTML={{ __html: description }} />)
                  )}
                  {pipe(
                    steps.message,
                    O.foldMap(react.monoid)(s => <StepHeading key="step-message" n={s.n} text={s.text} />)
                  )}
                  <AudioSegments
                    value={segments}
                    onChange={setSegments}
                    productId={productId}
                    config={config}
                    options={options}
                  />
                  {pipe(
                    starGreeting,
                    RR.foldMapWithIndex(s.Ord)(react.monoid)((key, data) => (
                      <StarGreeting
                        id={key}
                        title={data.title}
                        selected={data.selected}
                        onTextChange={onStarGreetingChange(key)}
                        onToggle={onStarGreetingSelectToggle(String(data.id))}
                        description={data.description}
                      />
                    ))
                  )}
                  {pipe(
                    steps.preview,
                    O.foldMap(react.monoid)(s => <StepHeading key="step-preview" n={s.n} text={s.text} />)
                  )}
                  {pipe(
                    options,
                    RD.fold3(
                      () => <></>,
                      () => <></>,
                      options =>
                        pipe(
                          options?.buttonLabel,
                          O.fromNullable,
                          O.chainEitherK(label => t.NonEmptyString.decode(label.btn_preview)),
                          O.getOrElse(() => 'Vorschau erstellen'),
                          label => (
                            <PreviewButton
                              size="lg"
                              disabled={b.not(isOrderable) || RD.isPending(ttsState)}
                              onClick={onPreviewClick}>
                              {label}
                              <FontAwesomeIcon icon={faRocket} className="ms-3" />
                            </PreviewButton>
                          )
                        )
                    )
                  )}
                </Col>
              </Row>
            ),
            () => (
              <Loading>
                <h3>Vorschau wird erstellt</h3>
              </Loading>
            ),
            () => <PreviewError onRetry={resetPreviewSession} />,
            ({ preview_url, transientId }) => {
              const displayImage = pipe(
                userImage,
                RR.values,
                RA.findFirstMap(data =>
                  pipe(
                    data.previewUrl,
                    O.alt(() => data.remoteUrl)
                  )
                ),
                O.getOrElseW(() => image)
              )

              return (
                <Row>
                  <Col md={4} className="d-flex flex-column align-items-center">
                    <div className="px-md-5">
                      <Wrapper>
                        <Image src={displayImage} />
                        <Overlay />
                        <AudioPlayer.Simple src={preview_url} />
                      </Wrapper>
                    </div>
                    <DownloadButton
                      target="_blank"
                      href={`${config.wpApiBase}/indiaudio/download/?url=${encodeURIComponent(preview_url)}`}
                      download={`${name}.mp3`}>
                      Download
                      <FontAwesomeIcon icon={faDownload} className="ms-3" />
                    </DownloadButton>
                  </Col>
                  <Col md={7} className="d-flex flex-column align-items-start justify-content-center gap-3 ps-md-5 mt-5 mt-md-0">
                    {pipe(
                      O.fromNullable(description),
                      O.foldMap(react.monoid)(description => <Description dangerouslySetInnerHTML={{ __html: description }} />)
                    )}
                    <ShareWidget url={`${config.homeUrl}?preview_id=${transientId}`} imageUrl={displayImage} />
                    <OrderButton size="lg" disabled={b.not(isOrderable)} onClick={onOrderClick}>
                      {`Jetzt bestellen für ${fmtEur.format(total)}`}
                      <FontAwesomeIcon icon={faShoppingCart} className="ms-3" />
                    </OrderButton>
                  </Col>
                </Row>
              )
            }
          )
        )
      )}
    </Container>
  )
}
